import { asyncCatch, getLabelFromStorage, consoleEvents } from '@pb/utils'
import { batch } from 'redux-act'

import {
  CHECKOUT_TRACKING_ERROR_TYPES,
  CHECKOUT_TRACKING_SECTIONS,
  SHOW_NOTIFICATION_TIME,
  TECHNICAL_ERROR_LABELS,
  ADDRESS_NOT_VALIDATED,
  ADDRESS_SECTION_STATES,
  ADDRESS_VALIDATED,
} from 'features/checkout/constants'
import {
  hideAddressSuggestions,
  postNewAddressFail,
  postNewAddressStart,
  postNewAddressSuccess,
  setAddressSuggestions,
  showAddressSuggestions,
  updateAddressFail,
  updateAddressStart,
  updateAddressSuccess,
} from 'features/checkout/duck/actions'
import {
  normalizeAddressOperationResponseV2,
  normalizeContextPartial,
} from 'features/checkout/duck/normalize'
import { findDeliveryAddress } from 'features/checkout/helpers'
import { postNewAddress, updateAddress, addressValidate } from 'features/checkout/requests'
import { Address, DeliveryAddressFormData, AddressResponseData } from 'features/checkout/types'
import { showNotification } from 'features/notifications/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'
import { ApiError } from 'types'

import { operationUpdateDeliveryModes } from '../delivery'
import { trackError } from '../track'

export const operationPerformAddressRequest =
  (
    addressDetails: DeliveryAddressFormData,
    addressId?: string,
    showSection?: (section: string) => void,
  ): ThunkResult<Promise<void>> =>
  async (dispatch) => {
    const startAction = addressId ? updateAddressStart : postNewAddressStart
    const successAction = addressId ? updateAddressSuccess : postNewAddressSuccess
    const failAction = addressId ? updateAddressFail : postNewAddressFail
    let addressPropsFromValidate = {}
    dispatch(startAction())

    if (!addressDetails.alreadyValidated && showSection) {
      try {
        const validateAddressResponse = await addressValidate(addressDetails)
        if (validateAddressResponse.decision === ADDRESS_NOT_VALIDATED) {
          const addressSuggestions = normalizeAddressOperationResponseV2(validateAddressResponse)
          if (addressSuggestions) {
            dispatch(setAddressSuggestions(addressSuggestions))
            dispatch(showAddressSuggestions())
            showSection(ADDRESS_SECTION_STATES.validateDelivery)
            dispatch(failAction([]))
            return
          }
        }
        // TODO: Replace full address object from address_validate response once id is sent from BE [COWEB-2907]
        else if (validateAddressResponse.decision === ADDRESS_VALIDATED) {
          const validatedADAddress = validateAddressResponse?.addressSuggestions?.[0]
          const isPoBox = validatedADAddress?.isPoBox
          addressPropsFromValidate = {
            ...addressPropsFromValidate,
            ...(typeof isPoBox !== 'undefined' && { isPoBox }),
          }
        }
      } catch (error) {
        consoleEvents.error('Failed to validate address', error)
      }
    }
    dispatch(hideAddressSuggestions())
    const requestFn = (requestBody: DeliveryAddressFormData): Promise<AddressResponseData> =>
      addressId ? updateAddress(addressId, requestBody) : postNewAddress(requestBody)
    try {
      const addressOperationResponse = await requestFn({
        ...addressDetails,
        ...addressPropsFromValidate,
      })
      const normalizedContext = dispatch(normalizeContextPartial(addressOperationResponse))

      dispatch(successAction(normalizedContext))
      await dispatch(
        operationUpdateDeliveryModes(
          findDeliveryAddress(normalizedContext?.customer?.currentAddresses),
        ),
      )
    } catch (error) {
      consoleEvents.error('Failed to update address', error)

      const { isClientError, isTechnicalError, code } = getErrorData(error)

      if (isClientError) {
        const translatedErrors = error.response.data.map((fieldError: ApiError) => ({
          ...fieldError,
          message: fieldError.message && getLabelFromStorage(fieldError.message),
        }))
        dispatch(failAction(translatedErrors))
      }
      if (isTechnicalError) {
        dispatch(
          batch(
            failAction([]),
            hideAddressSuggestions(),
            showNotification({
              content: getLabelFromStorage(TECHNICAL_ERROR_LABELS.updateAddress),
              type: 'error',
              timespan: SHOW_NOTIFICATION_TIME,
            }),
          ),
        )
      }
      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.deliveryLocation,
          message: getLabelFromStorage(TECHNICAL_ERROR_LABELS.updateAddress),
          id: code,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
    }
  }

export const operationSaveCardholderAddress =
  (addressDetails: Address): ThunkResult<Promise<void>> =>
  async (dispatch) => {
    dispatch(postNewAddressStart())
    const [error, context] = await asyncCatch(
      postNewAddress({ ...addressDetails, type: 'PAYMENT' }),
    )
    const normalizedContext = dispatch(normalizeContextPartial(context))
    if (context) {
      dispatch(postNewAddressSuccess(normalizedContext))
    }
    if (error) {
      const { isClientError, isTechnicalError, code, message, target } = getErrorData(error)
      if (isClientError) {
        dispatch(
          postNewAddressFail([
            {
              code,
              message,
              target,
            },
          ]),
        )
        if (isTechnicalError) {
          dispatch(postNewAddressFail([]))
        }
      }
    }
  }
